import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUserPrint } from '@app/hooks/useUserPrint';
import { getAccessToken } from '@app/utils/accessTokenStorage';

import { PROXY_SESSION_MESSAGE } from '../constants';
import {
  isProxyReadyMessage,
  isProxyRouteMessage,
} from '../utils/bridgeMessages';
import { getProxyCrmPath, getProxyFramePath } from '../utils/frame';

import { useProxyAccess } from './useProxyAccess';
import { useProxyIntegrationEffects } from './useProxyIntegrationEffects';

import type { ProxyIntegrationError } from '../types';

export const useProxyIntegration = () => {
  const { params } = useUserPrint();
  const { Database, UserId } = params;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { allowed, environment, origin } = useProxyAccess();
  const [attempt, setAttempt] = useState(0);
  const [readyFrameKey, setReadyFrameKey] = useState<string | null>(null);
  const [timedOutFrameKey, setTimedOutFrameKey] = useState<string | null>(null);
  const processedSessionsRef = useRef<{
    frameKey: string;
    noncesBySource: WeakMap<Window, Set<string>>;
  } | null>(null);

  const path = useMemo(
    () => getProxyFramePath(pathname, search),
    [pathname, search]
  );
  const error: ProxyIntegrationError | null = !allowed
    ? 'access-denied'
    : !origin || !environment
    ? 'not-configured'
    : null;

  // Путь и филиал не входят в ключ: смена экрана не пересоздаёт кабинет.
  const frameKey = `${environment}:${Database}:${UserId}:${origin}:${attempt}`;
  const isFrameEnabled = error === null;
  const isHandshakePending = isFrameEnabled && readyFrameKey !== frameKey;
  const isHandshakeTimedOut =
    isHandshakePending && timedOutFrameKey === frameKey;

  const messageHandler = useCallback(
    (data: unknown, source: Window) => {
      if (!allowed || !environment || !origin) return;

      // IframePage передаёт сюда сообщения только от текущего contentWindow
      // и с точного origin кабинета.
      if (isProxyReadyMessage(data)) {
        const processedSessions =
          processedSessionsRef.current?.frameKey === frameKey
            ? processedSessionsRef.current
            : {
                frameKey,
                noncesBySource: new WeakMap<Window, Set<string>>(),
              };
        processedSessionsRef.current = processedSessions;
        const processedNonces =
          processedSessions.noncesBySource.get(source) ?? new Set<string>();

        if (processedNonces.has(data.nonce)) return;

        processedNonces.add(data.nonce);
        processedSessions.noncesBySource.set(source, processedNonces);
        setReadyFrameKey(frameKey);
        source.postMessage(
          {
            type: PROXY_SESSION_MESSAGE,
            nonce: data.nonce,
            accessToken: getAccessToken() ?? '',
            userId: UserId,
            database: Database,
            environment,
            path,
          },
          origin
        );

        return;
      }

      if (isProxyRouteMessage(data)) {
        navigate(getProxyCrmPath(data.path), { replace: true });
      }
    },
    [allowed, environment, origin, frameKey, UserId, Database, path, navigate]
  );

  const handshakeTimeoutHandler = useCallback(() => {
    setTimedOutFrameKey(frameKey);
  }, [frameKey]);

  const retryHandler = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  const resetHandshakeHandler = useCallback(() => {
    processedSessionsRef.current = null;
    setReadyFrameKey(null);
    setTimedOutFrameKey(null);
  }, []);

  useProxyIntegrationEffects({
    frameKey,
    isFrameEnabled,
    onResetHandshake: resetHandshakeHandler,
    isHandshakePending,
    onHandshakeTimeout: handshakeTimeoutHandler,
  });

  return {
    host: error === null ? origin ?? '' : '',
    error,
    frameKey,
    isHandshakeTimedOut,
    messageHandler,
    retryHandler,
  };
};
